import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField } from '../components/Inputs'
import { computeRoofing, DEFAULT_ROOFING, PITCH_FACTORS } from '../calculators/roofing'
import type { RoofingInputs, RoofPitch } from '../calculators/roofing'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/roofing/'
const CATEGORY = 'roofing-and-insulation'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  savePrefix: string
  pitchOptions: { v: RoofPitch; l: string }[]
  bundleOptions: { value: 3 | 4; label: string }[]
  footprintLabel: string
  footprintTip: string
  pitchLabel: string
  pitchTip: (factor: number) => string
  pitchFactorLine: (factor: number, area: number, squares: string) => string
  shingleTypeLabel: string
  shingleTypeTip: string
  iceWaterLabel: string
  iceWaterTip: string
  wasteTip: string
  ftSuffix: string
  wasteSummary: (footprint: number, factor: number, actual: number, squares: string) => string
  primaryName: string
  primaryLabel: string
  primaryQuery: string
  secondaryName: string
  secondaryLabel: string
  squareUnit: (qty: number) => string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: { strong: string; rest: string }[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Roofing Calculator — Bundles, Squares and Underlayment | RenoSheets',
  desc: 'Free roofing calculator. Enter house footprint and roof pitch for shingle bundles, squares, underlayment rolls, drip edge and ridge cap. No signup.',
  h1: 'Roofing Calculator',
  intro: 'Enter your house footprint and roof pitch for shingle bundles, squares, underlayment, drip edge and ridge cap.',
  savePrefix: 'Roofing',
  pitchOptions: [
    { v: '3/12', l: '3/12 — very low slope' },
    { v: '4/12', l: '4/12 — low slope' },
    { v: '5/12', l: '5/12 — common residential' },
    { v: '6/12', l: '6/12 — medium slope' },
    { v: '7/12', l: '7/12 — medium-steep' },
    { v: '8/12', l: '8/12 — steep' },
    { v: '9/12', l: '9/12 — steep' },
    { v: '10/12', l: '10/12 — very steep' },
    { v: '12/12', l: '12/12 — 45 degree' },
  ],
  bundleOptions: [
    { value: 3, label: '3 bundles/square — 3-tab shingles' },
    { value: 4, label: '4 bundles/square — architectural/dimensional (most common)' },
  ],
  footprintLabel: 'House footprint',
  footprintTip: 'Measure the length and width of the house at ground level — not the actual roof slope. The calculator multiplies by the pitch factor to get actual roof area.',
  pitchLabel: 'Roof pitch',
  pitchTip: (factor) => `Rise over 12 in run. 5/12 means the roof rises 5 in for every 12 in horizontal. Your current pitch factor is ${factor} — actual area = footprint × factor × 2 sides.`,
  pitchFactorLine: (factor, area, squares) => `Pitch factor: ${factor} · actual roof area: ${area} sq ft (${squares} squares)`,
  shingleTypeLabel: 'Shingle type',
  shingleTypeTip: 'Architectural (dimensional) shingles use 4 bundles per square and are the standard for new construction. 3-tab shingles use 3 bundles per square.',
  iceWaterLabel: 'Add ice & water shield at eaves',
  iceWaterTip: 'Required by code in cold climates (most of Canada and northern US). Self-adhesive membrane applied along the first 3 ft of each eave.',
  wasteTip: '15% is standard for a simple gable roof. Use 20% for hip roofs, dormers, skylights and complex rooflines.',
  ftSuffix: 'ft',
  wasteSummary: (footprint, factor, actual, squares) => `${footprint} sq ft footprint × ${factor} × 2 sides = ${actual} sq ft actual → ${squares} squares with waste`,
  primaryName: 'Shingle bundles',
  primaryLabel: '',
  primaryQuery: 'architectural shingles bundle owens corning',
  secondaryName: 'Roofing squares',
  secondaryLabel: 'squares (100 sq ft each)',
  squareUnit: () => 'square',
  aboutH2: 'What actually affects how much roofing you need',
  aboutP1: 'Roof complexity is the single biggest driver of waste, far more than roof size. A simple gable roof with no valleys, dormers, or penetrations wastes relatively little — most cuts come from trimming at the edges. A hip roof, or any roof with multiple valleys, skylights, chimneys, or dormers, forces far more angled cuts, and each valley or hip needs its own flashing and starter treatment, which pushes waste from the standard 10-15% up to 20% or more. Pitch also affects material choice, not just the area calculation: very low-slope roofs (under 3-in-12) usually can\'t use standard asphalt shingles at all and need a different roofing system entirely, since water doesn\'t shed fast enough. Underlayment and starter strips are often forgotten in a materials list, but skipping proper starter shingles at eaves and rakes is one of the most common causes of early wind damage, since the first course has nothing to bond to without it.',
  howH2: 'How to calculate roofing shingles',
  howP1: 'Roofing starts with converting your house footprint to actual roof area using a pitch factor:',
  formula: `actual area = footprint (sq ft) × pitch factor × 2 (both sides)
area with waste = actual area × (1 + waste%)
squares = area with waste ÷ 100
bundles = ceil(squares × bundles per square)`,
  howP2: 'Pitch factors: 5/12 = 1.083, 6/12 = 1.118, 8/12 = 1.202, 12/12 = 1.414. Architectural shingles (4 bundles/sq) dominate new construction. Underlayment is 1 roll per 400 sq ft; ridge cap is 1 bundle per 35 lin ft of ridge.',
  mistakesH3: 'Common mistakes',
  mistakes: [
    { strong: 'Measuring the footprint area', rest: ' from inside the house (measure the roof overhang too).' },
    { strong: 'Forgetting ridge cap shingles', rest: ' — these are separate from field shingles and must be pre-cut or bought as hip & ridge bundles.' },
    { strong: 'Skipping ice & water shield', rest: ' in cold climates — ice dams will push water back under standard shingles.' },
    { strong: 'Not installing drip edge', rest: ' along eaves before underlayment — water runs behind fascia without it.' },
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: footprint × pitch factor × 2 sides ÷ 100 for squares; pitch factors from standard trigonometric values. Last updated 2026-05-26. Estimates only — verify quantities with your contractor before purchasing.',
  faqs: [
    { q: 'How many bundles of shingles do I need for 1000 square feet?', a: 'A 1000 sq ft house footprint with a 5/12 pitch has about 2166 sq ft of actual roof area (both sides). At 4 bundles per square (100 sq ft) and 15% waste, you need about 100 bundles. Architectural shingles are 4 bundles per square; 3-tab shingles are 3 bundles per square.' },
    { q: 'What is a roofing square?', a: 'A roofing square equals 100 square feet of actual roof surface area. It is the standard unit used by roofing contractors and suppliers. To get squares, divide your total roof area in sq ft by 100. A 2000 sq ft roof is 20 squares.' },
    { q: 'How do I calculate the roof area from the house footprint?', a: 'Multiply the house footprint area by the pitch factor for your roof slope, then multiply by 2 for both sides of a gable roof. For example, a 40 × 30 ft house (1200 sq ft footprint) with 5/12 pitch: 1200 × 1.083 × 2 = 2599 sq ft actual roof area.' },
    { q: 'What is the difference between 3-tab and architectural shingles?', a: 'Three-tab shingles are flat, lightweight and less expensive — they use 3 bundles per square. Architectural (dimensional) shingles have a layered, textured look and are thicker. They use 4 bundles per square and typically carry a 30–50 year warranty vs 25 years for 3-tab. Most new residential installs use architectural shingles.' },
    { q: 'Do I need ice and water shield?', a: 'Ice and water shield is required in cold climates (IECC Climate Zones 5–7, most of Canada) along the first 3 ft of each eave and at all valleys. It is a self-adhesive rubberized membrane that prevents water from ice dams backing up under shingles. Even in mild climates, using it in valleys and around chimneys is good practice.' },
    { q: 'How much does a bundle of architectural shingles cover?', a: 'One bundle of architectural shingles covers about 25 sq ft, and 4 bundles cover one roofing square (100 sq ft). Three-tab shingles use 3 bundles per square, so each of those bundles covers about 33.3 sq ft. Always check the bundle label for exact coverage.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de toiture — Paquets, carrés et sous-couche | RenoSheets',
  desc: 'Calculateur de toiture gratuit. Entrez l’empreinte de la maison et la pente du toit pour les paquets de bardeaux, les carrés, les rouleaux de sous-couche, le larmier et les bardeaux de faîte. Sans inscription.',
  h1: 'Calculateur de toiture',
  intro: 'Entrez l’empreinte de votre maison et la pente du toit pour les paquets de bardeaux, les carrés, la sous-couche, le larmier et les bardeaux de faîte.',
  savePrefix: 'Toiture',
  pitchOptions: [
    { v: '3/12', l: '3/12 — pente très faible' },
    { v: '4/12', l: '4/12 — pente faible' },
    { v: '5/12', l: '5/12 — résidentiel courant' },
    { v: '6/12', l: '6/12 — pente moyenne' },
    { v: '7/12', l: '7/12 — moyennement raide' },
    { v: '8/12', l: '8/12 — raide' },
    { v: '9/12', l: '9/12 — raide' },
    { v: '10/12', l: '10/12 — très raide' },
    { v: '12/12', l: '12/12 — 45 degrés' },
  ],
  bundleOptions: [
    { value: 3, label: '3 paquets/carré — bardeaux à 3 pattes' },
    { value: 4, label: '4 paquets/carré — architecturaux/dimensionnels (les plus courants)' },
  ],
  footprintLabel: 'Empreinte de la maison',
  footprintTip: 'Mesurez la longueur et la largeur de la maison au niveau du sol — pas la pente réelle du toit. Le calculateur multiplie par le facteur de pente pour obtenir la surface réelle du toit.',
  pitchLabel: 'Pente du toit',
  pitchTip: (factor) => `Montée sur 12 po de course. 5/12 signifie que le toit monte de 5 po par 12 po à l’horizontale. Votre facteur de pente actuel est ${factor} — surface réelle = empreinte × facteur × 2 versants.`,
  pitchFactorLine: (factor, area, squares) => `Facteur de pente : ${factor} · surface réelle du toit : ${area} pi² (${squares} carrés)`,
  shingleTypeLabel: 'Type de bardeaux',
  shingleTypeTip: 'Les bardeaux architecturaux (dimensionnels) utilisent 4 paquets par carré et sont la norme en construction neuve. Les bardeaux à 3 pattes utilisent 3 paquets par carré.',
  iceWaterLabel: 'Ajouter une membrane pare-glace et pare-eau aux avant-toits',
  iceWaterTip: 'Exigée par le code en climat froid (presque tout le Canada et le nord des États-Unis). Membrane autocollante posée sur les 3 premiers pi de chaque avant-toit.',
  wasteTip: '15 % est standard pour un toit à deux versants simple. Utilisez 20 % pour les toits en croupe, les lucarnes, les puits de lumière et les toitures complexes.',
  ftSuffix: 'pi',
  wasteSummary: (footprint, factor, actual, squares) => `empreinte de ${footprint} pi² × ${factor} × 2 versants = ${actual} pi² réels → ${squares} carrés avec perte`,
  primaryName: 'Paquets de bardeaux',
  primaryLabel: '',
  primaryQuery: 'architectural shingles bundle owens corning',
  secondaryName: 'Carrés de toiture',
  secondaryLabel: 'carrés (100 pi² chacun)',
  squareUnit: (qty) => (Math.abs(qty) === 1 ? 'carré' : 'carrés'),
  aboutH2: 'Ce qui influence vraiment la quantité de bardeaux nécessaire',
  aboutP1: 'La complexité du toit est le plus grand facteur de perte, bien plus que la taille du toit. Un toit à deux versants simple, sans vallées, lucarnes ni pénétrations, gaspille relativement peu — la plupart des coupes viennent de la finition des bords. Un toit en croupe, ou tout toit avec plusieurs vallées, puits de lumière, cheminées ou lucarnes, force beaucoup plus de coupes angulaires, et chaque vallée ou croupe demande son propre solin et traitement de départ, ce qui pousse la perte du standard de 10-15 % à 20 % ou plus. La pente influence aussi le choix du matériau, pas seulement le calcul de surface : les toits à très faible pente (moins de 3-en-12) ne peuvent généralement pas utiliser de bardeaux d’asphalte standards du tout et ont besoin d’un tout autre système de toiture, car l’eau ne s’évacue pas assez vite. Le sous-toit et les bandes de départ sont souvent oubliés dans une liste de matériaux, mais sauter les bardeaux de départ appropriés aux avant-toits et aux rives est l’une des causes les plus courantes de dommages précoces causés par le vent, puisque le premier rang n’a rien où adhérer sans eux.',
  howH2: 'Comment calculer les bardeaux de toiture',
  howP1: 'La toiture commence par convertir l’empreinte de la maison en surface réelle du toit à l’aide d’un facteur de pente :',
  formula: `surface réelle = empreinte (pi²) × facteur de pente × 2 (deux versants)
surface avec perte = surface réelle × (1 + % perte)
carrés = surface avec perte ÷ 100
paquets = arrondi.sup(carrés × paquets par carré)`,
  howP2: 'Facteurs de pente : 5/12 = 1,083, 6/12 = 1,118, 8/12 = 1,202, 12/12 = 1,414. Les bardeaux architecturaux (4 paquets/carré) dominent la construction neuve. La sous-couche couvre 1 rouleau par 400 pi²; le faîte est de 1 paquet par 35 pi linéaires de faîte.',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    { strong: 'Mesurer l’empreinte', rest: ' depuis l’intérieur de la maison (mesurez aussi le débord de toit).' },
    { strong: 'Oublier les bardeaux de faîte', rest: ' — ils sont distincts des bardeaux de champ et doivent être pré-coupés ou achetés en paquets d’arête et de faîte.' },
    { strong: 'Sauter la membrane pare-glace et pare-eau', rest: ' en climat froid — les barrages de glace repoussent l’eau sous les bardeaux ordinaires.' },
    { strong: 'Ne pas poser de larmier', rest: ' le long des avant-toits avant la sous-couche — sans lui, l’eau coule derrière la fascia.' },
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : empreinte × facteur de pente × 2 versants ÷ 100 pour les carrés; facteurs de pente issus des valeurs trigonométriques standards. Dernière mise à jour 2026-05-26. Estimations seulement — vérifiez les quantités avec votre entrepreneur avant d’acheter.',
  faqs: [
    { q: 'Combien de paquets de bardeaux pour 1000 pieds carrés?', a: 'Une empreinte de maison de 1000 pi² avec une pente de 5/12 a environ 2166 pi² de surface réelle de toit (les deux versants). À 4 paquets par carré (100 pi²) et 15 % de perte, il faut environ 100 paquets. Les bardeaux architecturaux comptent 4 paquets par carré; les bardeaux à 3 pattes, 3 paquets par carré.' },
    { q: 'Qu’est-ce qu’un carré de toiture?', a: 'Un carré de toiture équivaut à 100 pieds carrés de surface réelle de toit. C’est l’unité standard utilisée par les couvreurs et les fournisseurs. Pour obtenir les carrés, divisez votre surface totale de toit en pi² par 100. Un toit de 2000 pi² fait 20 carrés.' },
    { q: 'Comment calculer la surface du toit à partir de l’empreinte de la maison?', a: 'Multipliez la surface de l’empreinte de la maison par le facteur de pente de votre toit, puis multipliez par 2 pour les deux versants d’un toit à deux pentes. Par exemple, une maison de 40 × 30 pi (empreinte de 1200 pi²) avec une pente de 5/12 : 1200 × 1,083 × 2 = 2599 pi² de surface réelle de toit.' },
    { q: 'Quelle est la différence entre les bardeaux à 3 pattes et architecturaux?', a: 'Les bardeaux à 3 pattes sont plats, légers et moins coûteux — ils utilisent 3 paquets par carré. Les bardeaux architecturaux (dimensionnels) ont un aspect texturé en couches et sont plus épais. Ils utilisent 4 paquets par carré et offrent généralement une garantie de 30 à 50 ans contre 25 ans pour les 3 pattes. La plupart des nouvelles installations résidentielles utilisent des bardeaux architecturaux.' },
    { q: 'Ai-je besoin d’une membrane pare-glace et pare-eau?', a: 'La membrane pare-glace et pare-eau est exigée en climat froid (zones climatiques IECC 5 à 7, presque tout le Canada) sur les 3 premiers pi de chaque avant-toit et dans toutes les noues. C’est une membrane caoutchoutée autocollante qui empêche l’eau des barrages de glace de refouler sous les bardeaux. Même en climat doux, l’utiliser dans les noues et autour des cheminées est une bonne pratique.' },
    { q: 'Quelle surface couvre un paquet de bardeaux architecturaux?', a: 'Un paquet de bardeaux architecturaux couvre environ 25 pi², et 4 paquets couvrent un carré de toiture (100 pi²). Les bardeaux à 3 pattes utilisent 3 paquets par carré, donc chacun de ces paquets couvre environ 33,3 pi². Vérifiez toujours l’étiquette du paquet pour la couverture exacte.' },
  ],
}

export default function RoofingCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<RoofingInputs>(DEFAULT_ROOFING)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeRoofing(i, locale), [i, locale])
  const set = (patch: Partial<RoofingInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }

  const sqft = locale === 'fr' ? 'pi²' : 'sq ft'

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'roofing',
      name: `${c.savePrefix} — ${r.footprintArea} ${sqft}`,
      createdAt: new Date().toISOString(),
      summary: `${r.bundles} · ${r.squares.toFixed(1)} · ${r.underlaymentRolls}`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const pitchFactor = PITCH_FACTORS[i.pitch]
  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'roofing')
  const squaresStr = r.squares.toFixed(1)

  const primaryLabel = locale === 'fr'
    ? `à ${i.bundlesPerSquare} paquets/carré`
    : `at ${i.bundlesPerSquare} bundles/square`
  const assumptions = locale === 'fr'
    ? `empreinte de ${r.footprintArea} pi² · pente ${i.pitch} · ${r.actualRoofArea} pi² réels · ${i.wastePct} % de perte.`
    : `${r.footprintArea} sq ft footprint · ${i.pitch} pitch · ${r.actualRoofArea} sq ft actual · ${i.wastePct}% waste.`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeRoofing: footprint × pitch factor × 2 sides, × (1 + waste%),
  // ÷ 100 = squares, × bundles/square = bundles.
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {locale === 'fr'
          ? `Empreinte: ${i.footprintLengthFt} × ${i.footprintWidthFt}`
          : `Footprint: ${i.footprintLengthFt} × ${i.footprintWidthFt}`}
        {' '}= <strong>{r.footprintArea} {sqft}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `× facteur de pente ${pitchFactor} (${i.pitch}) × 2 versants`
          : `× pitch factor ${pitchFactor} (${i.pitch}) × 2 sides`}
        {' '}= <strong>{r.actualRoofArea} {sqft}</strong>
      </li>
      <li>
        {locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}:
        {' '}<strong>{Math.round(r.actualRoofArea * (1 + i.wastePct / 100))} {sqft}</strong>
      </li>
      <li>
        {locale === 'fr' ? '÷ 100 pi²/carré' : '÷ 100 sq ft/square'} = <strong>{squaresStr} {locale === 'fr' ? c.squareUnit(r.squares) : 'squares'}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `× ${i.bundlesPerSquare} paquets/carré`
          : `× ${i.bundlesPerSquare} bundles/square`}
        {' '}= <strong>{r.bundles} {locale === 'fr' ? (r.bundles === 1 ? 'paquet' : 'paquets') : (r.bundles === 1 ? 'bundle' : 'bundles')}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Faîte: ${i.footprintLengthFt} pi ÷ 35 pi/paquet`
          : `Ridge: ${i.footprintLengthFt} ft ÷ 35 ft/bundle`}
        {' '}= <strong>{r.ridgeCapBundles}</strong>
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('roofing', 'Roofing Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Roofing & Insulation', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('roofing', 'Roofing Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>

          {/* Footprint */}
          <div className="rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              {c.footprintLabel}
              <InfoTip text={c.footprintTip} />
            </p>
            <div className="grid grid-cols-2 gap-2">
              <SmartField label={tr('calc.length')} imperialValue={i.footprintLengthFt} onChangeImperial={(n) => set({ footprintLengthFt: n })} imperialUnit="ft" />
              <SmartField label={tr('calc.width')} imperialValue={i.footprintWidthFt} onChangeImperial={(n) => set({ footprintWidthFt: n })} imperialUnit="ft" />
            </div>
          </div>

          {/* Pitch */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.pitchLabel}
              <InfoTip text={c.pitchTip(pitchFactor)} />
            </span>
            <select
              value={i.pitch}
              onChange={(e) => set({ pitch: e.target.value as RoofPitch })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2.5 text-sm text-text focus:border-brand focus:outline-none"
            >
              {c.pitchOptions.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-subtle">{c.pitchFactorLine(pitchFactor, r.actualRoofArea, squaresStr)}</p>
          </div>

          {/* Shingle type */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.shingleTypeLabel}
              <InfoTip text={c.shingleTypeTip} />
            </span>
            <select
              value={i.bundlesPerSquare}
              onChange={(e) => set({ bundlesPerSquare: parseInt(e.target.value) as 3 | 4 })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2.5 text-sm text-text focus:border-brand focus:outline-none"
            >
              {c.bundleOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Ice & water */}
          <label className="mt-4 flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={i.needIceWater} onChange={(e) => set({ needIceWater: e.target.checked })} />
            {c.iceWaterLabel}
            <InfoTip text={c.iceWaterTip} />
          </label>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={10} max={20} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">{c.wasteSummary(r.footprintArea, pitchFactor, r.actualRoofArea, squaresStr)}</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: primaryLabel, qty: r.bundles, unit: 'bundle', query: c.primaryQuery }}
            secondary={{ name: c.secondaryName, label: c.secondaryLabel, qty: r.squares, unit: c.squareUnit(r.squares), query: 'roofing square' }}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}><strong>{m.strong}</strong>{m.rest}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
